import React, { useState, useEffect } from "react";
import Styles from "./ConfigContainer.module.css";
import { useSelector } from "react-redux";
import { ReactComponent as ViewCharge } from "../../../../assets/images/ViewCharge.svg";
import { ReactComponent as ViewContract } from "../../../../assets/images/ViewContract.svg";
import { ReactComponent as ViewTransaction } from "../../../../assets/images/ViewTransaction.svg";
import { ReactComponent as Beneficary } from "../../../../assets/images/Beneficary.svg";
import { ReactComponent as BenificaryExit } from "../../../../assets/images/BenificaryExit.svg";
import { ReactComponent as EscrowRupee } from "../../../../assets/images/EscrowRupee.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import ChargeView from "../../ChargeView/ChargeView";
import { ArrowForward } from "@mui/icons-material";

const user = [
  {
    type: "view_charges",
    name: "View Charges",
    svg: ViewCharge,
    viewCharges: true,
  },
  {
    type: "contract",
    name: "Contracts",
    svg: ViewContract,
    nav: "/wallet_contract",
  },
  {
    type: "view_transactions",
    name: "Transactions",
    svg: ViewTransaction,
    nav: "/wallet_transaction",
  },
];

const admin = [
  {
    type: "contract",
    name: "Contract",
    svg: ViewContract,
    nav: "/wallet_contract",
  },
  {
    type: "add_charges",
    name: "Add User Charges",
    svg: ViewContract,
  },
  {
    type: "create_charges",
    name: "Create Charges",
    svg: ViewTransaction,
  },
];

const formatIndianNumber = (num) => {
  if (num == null) return "₹0";
  return "₹" + num.toLocaleString("en-IN");
};

const AccountDetailCard = ({
  header,
  icon: Icon,
  account,
  loading,
  header2,
  infoTooltip,
  showPayeeName = false,
}) => (
  <div className={Styles.benificiaryDetailsCont}>
    <div className={Styles.beneficaryHeader}>
      <Icon style={{ height: "14px", width: "14px" }} />
      <span>{header}</span>
    </div>
    {infoTooltip && (
      <Tooltip title={`VID: ${infoTooltip}`} arrow placement="top">
        <InfoOutlinedIcon className={Styles.infoIcon} />
      </Tooltip>
    )}
    {loading ? (
      <div className={Styles.skeletonPad}>
        <Skeleton variant="text" width="55%" height={16} />
        <div className={Styles.accountGrid}>
          <Skeleton variant="text" width="80%" height={14} />
          <Skeleton variant="text" width="80%" height={14} />
          <Skeleton variant="text" width="70%" height={14} />
          <Skeleton variant="text" width="70%" height={14} />
        </div>
      </div>
    ) : (
      <div className={Styles.accountDetailsContent}>
        <div className={Styles.accountNumber}>
          <span className={Styles.accountNumberLabel}>{header2}:</span>
          <strong className={Styles.accountNumberValue}>
            {account?.accountNumber || account?.walletCompanyCode || "—"}
          </strong>
        </div>
        <div className={Styles.accountGrid}>
          {showPayeeName && (account?.payee || account?.payeeName) && (
            <div className={Styles.accountGridCell}>
              <div className={Styles.fieldLabel}>Payee Name:</div>
              <div
                className={Styles.fieldValue}
                title={account?.payee || account?.payeeName}
              >
                {account?.payee || account?.payeeName}
              </div>
            </div>
          )}
          <div className={Styles.accountGridCell}>
            <div className={Styles.fieldLabel}>Bank Name:</div>
            <div className={Styles.fieldValue} title={account?.bankName || ""}>
              {account?.bankName || "—"}
            </div>
          </div>
          <div className={Styles.accountGridCell}>
            <div className={Styles.fieldLabel}>IFSC Code:</div>
            <div className={Styles.fieldValue} title={account?.ifscCode || ""}>
              {account?.ifscCode || "—"}
            </div>
          </div>
          <div className={Styles.accountGridCell}>
            <div className={Styles.fieldLabel}>Branch:</div>
            <div
              className={Styles.fieldValue}
              title={account?.branchAddress || account?.branch || ""}
            >
              {account?.branchAddress || account?.branch || "—"}
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

const ConfigContainer = () => {
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  ).toLowerCase();
  const data = useSelector((state) => state?.paymentSlice?.walletAccountActive);
  const navigation = useNavigate();
  const [charges, setCharges] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (data !== undefined) setLoading(false);
  }, [data]);

  const handleViewCharges = () => setCharges(true);

  const payInAccount = data?.fundsPayInBankAccount;
  const receiveAccount = data?.fundsReceiverBankAccountDetails;

  const actions = role === "consumer" || role === "generator" ? user : admin;

  const balanceCards = [
    {
      label: "Total Virtual A/c Balance",
      value: formatIndianNumber(
        (data?.amountRemaining ?? 0) + (data?.amountBlocked ?? 0),
      ),
      icon: EscrowRupee,
    },
    {
      label: "Amount Blocked",
      value: formatIndianNumber(data?.amountBlocked),
      icon: EscrowRupee,
    },
    {
      label: "Balance Available",
      value: formatIndianNumber(data?.amountRemaining),
      icon: EscrowRupee,
    },
  ];

  return (
    <>
      {charges && (
        <ChargeView
          open={charges}
          handleClose={() => setCharges(false)}
          userLevel={true}
        />
      )}

      <div className={Styles.pricemainContainer}>
        {/* Left: Beneficiary Details */}
        <div
          className={`${Styles.benificiaryDetailMainCont} ${
            role === "generator" ? Styles.genBackColor : Styles.conBackColor
          }`}
        >
          <AccountDetailCard
            header="Beneficiary Details for Funds Pay-In"
            icon={Beneficary}
            account={payInAccount}
            loading={loading}
            header2="Virtual Account Number"
          />
          <AccountDetailCard
            header="Account Details for Receiving Funds"
            icon={BenificaryExit}
            account={receiveAccount}
            loading={loading}
            header2="Account Number"
            infoTooltip={data?.walletCompanyCode}
            showPayeeName={true}
          />
        </div>

        {/* Right: Balance + Actions */}
        <div
          className={`${Styles.benificiaryBalanceMainCont} ${
            role === "generator"
              ? Styles.genBackColorBalance
              : Styles.conBackColorBalance
          }`}
        >
          {/* Balance Cards */}
          <div className={Styles.balanceCardsRow}>
            {balanceCards.map(({ label, value, icon: Icon }, idx) => (
              <Tooltip
                key={idx}
                title={`${label}: ${value}`}
                arrow
                placement="top"
              >
                <div className={Styles.balanceCard}>
                  <Icon style={{ fontSize: "28px", flexShrink: 0 }} />
                  <div className={Styles.balanceCardText}>
                    <div className={Styles.balanceCardTitle}>{label}</div>
                    <div className={Styles.balanceCardValue}>{value}</div>
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>

          {/* Action Buttons */}
          <div className={Styles.actionButtonsRow}>
            {actions.map((ele, idx) => {
              const SvgIcon = ele.svg;
              return (
                <div
                  key={idx}
                  className={Styles.actionButton}
                  onClick={() =>
                    ele?.viewCharges
                      ? handleViewCharges()
                      : ele?.nav
                        ? navigation(ele?.nav)
                        : undefined
                  }
                >
                  <div className={Styles.actionButtonInner}>
                    <SvgIcon style={{ fontSize: "28px" }} />
                    <span>{ele?.name}</span>
                  </div>
                  <ArrowForward
                    style={{
                      fontSize: "24px",
                      color: "#727272",
                      flexShrink: 0,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfigContainer;
